"use client";

import * as React from "react";

import { Box } from "../../components/ui/box.tsx";
import { Text } from "../../components/ui/text.tsx";
import type { Omittable } from "../../lib/props.ts";

export interface PreviewRadioOption {
  /** The stored value this card selects. */
  value: string;
  /** What the viewer reads under the art. */
  label: string;
  /**
   * The preview drawing. Decorative — the label names the choice, so the art
   * carries no accessible text of its own.
   *
   * A NODE rather than an image source: the art that reads best here is drawn
   * (SVG), which stays sharp at any card width and needs no asset kept in sync
   * with the design.
   */
  art: React.ReactNode;
}

export type PreviewRadioGroupProps = Omittable<{
  options: readonly PreviewRadioOption[];
  value: string;
  onValueChange: (value: string) => void;
  /**
   * Hold three across and shrink the type, for a popup where the full-size
   * cards would stack. The art is drawn, so it loses nothing at this size —
   * which is the reason it can appear in both places at all.
   */
  compact?: boolean;
  /**
   * Cards across. Defaults to three (one on a phone, three from `sm`), the
   * appearance picker's shape; a pair of options asks for two.
   */
  columns?: 2 | 3 | 4;
  /**
   * Where the option's name reads. `below` (default) sets it under the card
   * in the panel's own type; `inside` prints it on the art's lower edge over a
   * dark scrim, so the card is the whole target and the group keeps one
   * rhythm with rows that carry no caption of their own.
   */
  labelPlacement?: "below" | "inside";
  "aria-label": string;
}>;

/**
 * A radio group whose options are PICTURES.
 *
 * For a choice that is itself visual — an appearance, a density, a layout —
 * where a word asks the viewer to imagine the result and a thumbnail simply
 * shows it. The art is decorative: the label under each card is the accessible
 * name, so a screen reader hears the choice rather than a description of a
 * picture.
 *
 * Deliberately not a variant of the plain radio group. That control answers
 * "which of these values"; this one answers "which of these looks right", and
 * the difference is why the art is mandatory rather than optional.
 */
export function PreviewRadioGroup({
  options,
  value,
  onValueChange,
  compact,
  columns,
  labelPlacement = "below",
  "aria-label": ariaLabel,
}: PreviewRadioGroupProps): React.ReactElement {
  const inside = labelPlacement === "inside";
  const selectedIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  // Arrow keys move the selection, which is what `role="radio"` promises. A
  // group of buttons that only answers to Tab and Space says it is a radio
  // group and then behaves like something else.
  const onKeyDown = (event: React.KeyboardEvent) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    // No empty-list guard: with zero options the group renders no focusable
    // child, so no keydown can originate inside it.
    if (step === 0) return;
    event.preventDefault();
    const next =
      (selectedIndex + step + options.length) % options.length;
    onValueChange(options[next]!.value);
  };

  return (
    <Box
      aria-label={ariaLabel}
      columns={columns ?? (compact ? 3 : { base: 1, sm: 3 })}
      display="grid"
      gap={compact || inside ? "xs" : "base"}
      onKeyDown={onKeyDown}
      role="radiogroup"
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          // The whole card is one hover target — the label sits OUTSIDE the
          // button, so it can only follow the pointer through a group.
          <Box key={option.value} direction="column" gap="xs" group="option">
            <Box
              aria-checked={selected}
              aria-label={option.label}
              as="button"
              // ONE edge. The art carries its own corners, so the card clips it
              // and the hairline is the only line — a border plus an offset
              // ring plus the bitmap's own radius read as three concentric
              // outlines with a halo between them. Selection thickens the SAME
              // edge to 2px with a contiguous OUTWARD ring — outward because a
              // wider border would shift the art by a pixel, and an inset ring
              // is a shadow painted beneath children, which the full-bleed art
              // covers completely.
              className={[
                "transition-[transform,border-color,box-shadow] duration-150 ease-out",
                "motion-safe:active:scale-[0.98]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected
                  ? "border-ring ring-1 ring-ring"
                  : "hover:border-border-strong",
              ].join(" ")}
              border
              onClick={() => onValueChange(option.value)}
              overflow="hidden"
              position={inside ? "relative" : undefined}
              role="radio"
              rounded="lg"
              // Only ONE card is in the tab order and the arrows move within
              // the group — the radio contract. `selectedIndex` falls back to
              // 0, so a value matching nothing still leaves a way in.
              tabIndex={index === selectedIndex ? 0 : -1}
              type="button"
              width="full"
            >
              {option.art}
              {inside ? (
                // The scrim is the caption's ground, not the card's: it rises
                // from the lower edge just far enough to seat one line of
                // white type over any picture. The button's aria-label already
                // names the option, so the caption is presentation.
                <Box
                  aria-hidden="true"
                  className="bg-linear-to-t from-black/62 to-transparent"
                  direction="row"
                  align="center"
                  paddingX="sm"
                  paddingTop="base"
                  paddingBottom="xs"
                  position="absolute"
                  insetX={0}
                  bottom={0}
                >
                  <Text className="text-white" size="xs" weight="medium">
                    {option.label}
                  </Text>
                </Box>
              ) : null}
            </Box>
            {inside ? null : (
            <Text
              align="center"
              // A quiet label brightens with its card, so the pair reads as one
              // target rather than a picture with a caption near it. The
              // selected label is already at full ink and has nowhere to go.
              className={
                selected
                  ? undefined
                  : "transition-colors duration-150 group-hover/option:text-foreground"
              }
              size={compact ? "xs" : "sm"}
              variant={selected ? undefined : "muted"}
              weight={selected ? "medium" : undefined}
            >
              {option.label}
            </Text>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
